import ExcelJS from 'exceljs';
import { toValidZimbabwePhone } from './phoneNumber';
import { excelCellToString } from './excelCell';

export interface PersonalizedRow {
  phone: string;
  fields: Record<string, string>;
}

export interface PersonalizePreviewRow {
  values: string[];
  phone: string | null;
  isValid: boolean;
}

export interface PersonalizeParseResult {
  columns: string[];
  rows: PersonalizedRow[];
  previewRows: PersonalizePreviewRow[];
  skipped: number;
  fileName: string;
}

function uniqueHeaders(raw: string[]): string[] {
  const seen = new Map<string, number>();
  return raw.map((heading, index) => {
    const trimmed = heading.trim();
    const name = trimmed || (index === 0 ? 'Mobile' : `Column${index + 1}`);
    const count = seen.get(name) || 0;
    seen.set(name, count + 1);
    return count > 0 ? `${name}_${count + 1}` : name;
  });
}

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === ',' || char === ';' || char === '\t') && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result.map((value) => value.trim());
}

function buildResult(
  headerCells: string[],
  dataRows: string[][],
  fileName: string
): PersonalizeParseResult {
  const hasHeader = headerCells.some((cell) => cell.trim().length > 0);
  if (!hasHeader) {
    throw new Error(
      'The first row must contain column headings. Please add a header row and try again.'
    );
  }

  const columns = uniqueHeaders(headerCells);
  const previewRows: PersonalizePreviewRow[] = [];
  const rows: PersonalizedRow[] = [];
  let skipped = 0;

  dataRows.forEach((cells) => {
    const isEmpty = cells.every((cell) => !String(cell || '').trim());
    if (isEmpty) return;

    const padded = columns.map((_, index) => String(cells[index] ?? '').trim());
    const phone = toValidZimbabwePhone(padded[0]);
    const fields: Record<string, string> = {};
    columns.forEach((column, index) => {
      fields[column] = padded[index] || '';
    });

    if (previewRows.length < 5) {
      previewRows.push({
        values: padded,
        phone,
        isValid: Boolean(phone)
      });
    }

    if (phone) {
      rows.push({ phone, fields });
    } else {
      skipped += 1;
    }
  });

  return { columns, rows, previewRows, skipped, fileName };
}

function parseCsvContent(content: string, fileName: string): PersonalizeParseResult {
  const lines = content.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) {
    throw new Error(
      'The first row must contain column headings. Please add a header row and try again.'
    );
  }
  const headerCells = splitCsvLine(lines[0]);
  const dataRows = lines.slice(1).map(splitCsvLine);
  return buildResult(headerCells, dataRows, fileName);
}

function readHeaderCells(worksheet: ExcelJS.Worksheet): string[] {
  const headerRow = worksheet.getRow(1);
  const cells: string[] = [];
  let lastNonEmpty = 0;

  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const text = excelCellToString(cell.value).trim();
    cells[colNumber - 1] = text;
    if (text) lastNonEmpty = colNumber;
  });

  return cells.slice(0, Math.max(lastNonEmpty, 1)).map((value) => value || '');
}

function readDataCells(row: ExcelJS.Row, columnCount: number): string[] {
  const cells: string[] = [];
  for (let col = 1; col <= columnCount; col += 1) {
    cells.push(excelCellToString(row.getCell(col).value).trim());
  }
  return cells;
}

async function parseExcelBuffer(
  data: ArrayBuffer,
  fileName: string
): Promise<PersonalizeParseResult> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(data);
  const worksheet = workbook.worksheets[0];
  if (!worksheet) {
    throw new Error('The Excel file does not contain a worksheet.');
  }

  const headerCells = readHeaderCells(worksheet);
  const lastRow = worksheet.actualRowCount || worksheet.rowCount || 1;
  const dataRows: string[][] = [];
  const seenRows = new Set<string>();

  for (let rowNumber = 2; rowNumber <= lastRow; rowNumber += 1) {
    const cells = readDataCells(worksheet.getRow(rowNumber), headerCells.length);
    if (cells.every((cell) => !cell)) continue;

    const signature = cells.join('\u0001');
    if (seenRows.has(signature)) continue;
    seenRows.add(signature);
    dataRows.push(cells);
  }

  return buildResult(headerCells, dataRows, fileName);
}

export async function parsePersonalizeFile(file: File): Promise<PersonalizeParseResult> {
  const lower = file.name.toLowerCase();

  if (lower.endsWith('.csv') || lower.endsWith('.txt')) {
    const content = await file.text();
    return parseCsvContent(content, file.name);
  }

  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
    const buffer = await file.arrayBuffer();
    return parseExcelBuffer(buffer, file.name);
  }

  throw new Error('Please upload a CSV or Excel file (.csv, .xlsx).');
}
